import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { additionalExpenses } from '@/api/additionalExpenses';
import { goodsReceipts } from '@/api/goodsReceipts';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { partyCodesText } from '@/shared/utils/partyCodes';
import SaveXlsxButton from '@/shared/components/SaveXlsxButton';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQty = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const TYPE_LABEL: Record<string, string> = {
  TRANSPORT: 'Транспортні витрати',
  INSURANCE: 'Страхування',
  CUSTOMS_BROKER: 'Послуги митного брокера',
  OTHER: 'Інші додаткові витрати',
};

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}
function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export default function AdditionalExpensePrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['additional-expense', id ?? ''],
    queryFn: () => additionalExpenses.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const ctrQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });

  // Подгрузка всех поступлений организации (для получения названий товаров по строкам)
  const receiptsQ = useQuery({
    queryKey: ['goods-receipts-for-print', docQ.data?.organization_id ?? ''],
    queryFn: () => goodsReceipts.list({
      organization_id: docQ.data!.organization_id, posted: true, limit: 500,
    }),
    enabled: !!docQ.data,
  });
  const receiptIds = useMemo(
    () => receiptsQ.data?.items.map((r) => r.id) ?? [],
    [receiptsQ.data],
  );
  const fullReceipts = useQuery({
    queryKey: ['goods-receipts-full', receiptIds.join(',')],
    queryFn: () => Promise.all(receiptIds.map((rid) => goodsReceipts.get(rid))),
    enabled: receiptIds.length > 0,
  });

  const riMap = useMemo(() => {
    const m: Record<string, { nom: string; qty: string; amount: string }> = {};
    fullReceipts.data?.forEach((rcpt) => {
      rcpt.items.forEach((it) => {
        if (it.id) m[it.id] = {
          nom: it.nomenclature_id, qty: it.quantity, amount: it.amount_without_vat,
        };
      });
    });
    return m;
  }, [fullReceipts.data]);

  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc.organization_id);
  const ctr = ctrQ.data?.items.find((c) => c.id === doc.contractor_id);

  return (
    <div className="print-page" style={{
      padding: '20mm', fontFamily: 'Times, serif', fontSize: '11pt', lineHeight: 1.4,
      maxWidth: '210mm', margin: '0 auto', background: 'white',
    }}>
      <div className="print-hide" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button onClick={() => window.print()} className="primary">друкувати</button>
        <button onClick={() => window.history.back()}>назад</button>
        <SaveXlsxButton />
      </div>

      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '1rem 0 0.3rem' }}>
        Бухгалтерська довідка-розрахунок
      </h2>
      <h3 style={{ textAlign: 'center', fontSize: '11pt', margin: '0 0 1rem', fontWeight: 'normal' }}>
        розподілу додаткових витрат на придбання № {doc.document_number}
      </h3>
      <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '10pt', color: '#555' }}>
        від {formatDateUk(doc.document_date)}
      </div>

      <table style={{ width: '100%', marginBottom: '0.8rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '0.2rem 0.5rem', verticalAlign: 'top' }}>
              <strong>Вид витрати:</strong> {TYPE_LABEL[doc.expense_type] ?? doc.expense_type}
            </td>
            <td style={{ width: '50%', padding: '0.2rem 0.5rem', verticalAlign: 'top' }}>
              <strong>Постачальник послуги:</strong> {ctr?.name ?? '—'}
              {partyCodesText(ctr) && <div style={{ fontSize: '10pt' }}>{partyCodesText(ctr)}</div>}
            </td>
          </tr>
          {(doc.supplier_doc_number || doc.supplier_doc_date) && (
            <tr>
              <td colSpan={2} style={{ padding: '0.2rem 0.5rem' }}>
                <strong>Документ постачальника:</strong>{' '}
                {doc.supplier_doc_number ? `№ ${doc.supplier_doc_number}` : ''}{' '}
                {doc.supplier_doc_date ? `від ${formatShortDate(doc.supplier_doc_date)}` : ''}
              </td>
            </tr>
          )}
          <tr>
            <td style={{ padding: '0.2rem 0.5rem' }}>
              <strong>Сума без ПДВ:</strong> {fmt(doc.amount_without_vat)} грн
            </td>
            <td style={{ padding: '0.2rem 0.5rem' }}>
              <strong>ПДВ:</strong> {fmt(doc.vat_amount)} грн
            </td>
          </tr>
          <tr>
            <td colSpan={2} style={{ padding: '0.2rem 0.5rem', fontWeight: 700, borderTop: '1px solid #000' }}>
              Усього: {fmt(doc.total_amount)} грн
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: '1rem', marginBottom: '0.5rem', fontWeight: 600 }}>
        Розподіл суми послуги без ПДВ ({fmt(doc.amount_without_vat)} грн) між рядками надходження:
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
        <thead>
          <tr style={{ borderTop: '2px solid #000', borderBottom: '1px solid #000' }}>
            <th style={{ padding: '0.3rem', textAlign: 'center', width: '5%' }}>№</th>
            <th style={{ padding: '0.3rem', textAlign: 'left' }}>Товар з надходження</th>
            <th style={{ padding: '0.3rem', textAlign: 'left', width: '18%' }}>Постачальник</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '10%' }}>К-сть</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '20%' }}>База розподілу</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '20%' }}>+ до собівартості</th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((it) => {
            const ri = riMap[it.goods_receipt_item_id];
            const name = ri ? (nomName.get(ri.nom) ?? '—') : '(рядок надходження)';
            return (
              <tr key={it.id} style={{ borderBottom: '1px solid #ccc' }}>
                <td style={{ padding: '0.3rem', textAlign: 'center' }}>{it.line_number}</td>
                <td style={{ padding: '0.3rem' }}>{name}</td>
                <td style={{ padding: '0.3rem' }}>
                  {(it as { receipt_contractor_name?: string | null })
                    .receipt_contractor_name ?? '—'}
                </td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                  {ri ? fmtQty(ri.qty) : '—'}
                </td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.distribution_base)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(it.allocated_amount ?? '0')}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}>
            <td colSpan={4} style={{ padding: '0.3rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>
              {fmt(doc.items.reduce((s, i) => s + Number(i.distribution_base || 0), 0))}
            </td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>
              {fmt(doc.amount_without_vat)}
            </td>
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: '1.5rem', padding: '0.75rem', background: '#f8fafc', fontSize: '10pt' }}>
        <strong>Бухгалтерські проводки:</strong>
        <div style={{ marginTop: '0.3rem' }}>
          • Дт 281 — Кт 631 на {fmt(doc.amount_without_vat)} грн
          (по рядках, з збільшенням собівартості партій)
        </div>
        {Number(doc.vat_amount) > 0 && (
          <div>
            • Дт 6442 — Кт 631 на {fmt(doc.vat_amount)} грн (податковий кредит)
          </div>
        )}
        <div>
          • Реєстр взаєморозрахунків: виникнення заборгованості перед
          постачальником послуги на {fmt(doc.total_amount)} грн.
        </div>
      </div>

      <table style={{ width: '100%', marginTop: '2.5rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '0.5rem' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center' }}>
                ___________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%', padding: '0.5rem' }}>
              <div>Головний бухгалтер:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center' }}>
                ___________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
            </td>
          </tr>
        </tbody>
      </table>

    </div>
  );
}
